import { useState, useEffect } from 'react';
import { Dialog, Loading, RATINGS, api, useToast } from './_barrel.js';


        function AssessmentDialog({ token, subjectId, currentAssessment, supersede, onClose, onSuccess }) {
            const [autoScore, setAutoScore] = useState(null);
            const [autoLoading, setAutoLoading] = useState(!supersede);
            const [autoError, setAutoError] = useState(null);
            const [narrativeDraft, setNarrativeDraft] = useState(null);
            const [narrativeLoading, setNarrativeLoading] = useState(false);
            const [narrativeError, setNarrativeError] = useState(null);
            const [form, setForm] = useState({
                assessment_summary: '',
                recommendations: '',
                override_enabled: false,
                override_composite: '',
                override_reason: '',
            });
            const [rubric, setRubric] = useState(null);
            const [submitting, setSubmitting] = useState(false);
            const [error, setError] = useState(null);
            const [refreshing, setRefreshing] = useState(false);
            const [refreshMsg, setRefreshMsg] = useState(null);
            const toast = useToast();

            const reloadRubric = async () => {
                setAutoLoading(true); setAutoError(null);
                try { setRubric(await api(`/api/v1/assessments/rubric?subject_id=${subjectId}`, {}, token)); }
                catch (e) { setAutoError(e.message); }
                finally { setAutoLoading(false); }
            };

            // Fold-in of the old Auto-Enrich agent: refresh every source, then
            // re-score — so the assessment is signed against a current picture.
            const refreshAllSources = async () => {
                setRefreshing(true); setError(null); setRefreshMsg('Collecting from every source…');
                const side = Promise.allSettled([
                    api(`/api/v1/subjects/${subjectId}/emails/check`, { method: 'POST' }, token),
                    api(`/api/v1/subjects/${subjectId}/sanctions/check`, { method: 'POST' }, token),
                ]);
                try {
                    await api('/api/v1/run-now', { method: 'POST', body: JSON.stringify({ scope: 'subject', target_id: subjectId }) }, token);
                } catch (e) { /* a tenant run may already be in flight — poll it */ }
                let ticks = 0;
                await new Promise((resolve) => {
                    const iv = setInterval(async () => {
                        ticks += 1;
                        try {
                            const st = await api('/api/v1/run-now/status', {}, token);
                            setRefreshMsg(`Collecting${st.targets_total ? ` · ${st.targets_done}/${st.targets_total}` : ''}…`);
                            if (!st.in_flight || ticks > 80) { clearInterval(iv); resolve(); }
                        } catch (e) { if (ticks > 80) { clearInterval(iv); resolve(); } }
                    }, 3000);
                });
                try { await side; } catch (e) { /* breach/sanctions best-effort */ }
                setRefreshMsg('Re-scoring…');
                await reloadRubric();
                setRefreshing(false); setRefreshMsg(null);
                toast('Refreshed & re-scored from all sources');
            };

            const generateNarrative = async () => {
                setNarrativeLoading(true);
                setNarrativeError(null);
                try {
                    const result = await api(
                        `/api/v1/subjects/${subjectId}/narrative`,
                        { method: 'POST' },
                        token
                    );
                    setNarrativeDraft(result);
                    setForm(f => ({
                        ...f,
                        assessment_summary: result.summary,
                        recommendations: result.recommendations,
                    }));
                    toast('Narrative draft generated');
                } catch (err) {
                    setNarrativeError(err.message);
                } finally {
                    setNarrativeLoading(false);
                }
            };

            // Fetch auto-score on open. Runs for both new and supersede so
            // the analyst always sees a fresh suggestion based on current
            // signals, not just the values from the previous assessment.
            useEffect(() => {
                let cancelled = false;
                (async () => {
                    try {
                        const result = await api(`/api/v1/assessments/rubric?subject_id=${subjectId}`, {}, token);
                        if (cancelled) return;
                        setRubric(result);
                    } catch (err) {
                        if (!cancelled) setAutoError(err.message);
                    } finally {
                        if (!cancelled) setAutoLoading(false);
                    }
                })();
                return () => { cancelled = true; };
            }, [token, subjectId]);

            const bandFor = (c) => RATINGS.label(Number(c));   // canonical bands
            const overrideComposite = form.override_enabled && form.override_composite !== '' ? (parseFloat(form.override_composite) || 0) : null;
            const effComposite = overrideComposite != null ? overrideComposite : (rubric ? rubric.composite : 0);
            const effBand = overrideComposite != null ? bandFor(overrideComposite) : (rubric ? rubric.band : '');

            const submit = async (e) => {
                e.preventDefault();
                setSubmitting(true);
                setError(null);
                try {
                    const body = {
                        subject_id: subjectId,
                        assessment_summary: form.assessment_summary,
                        recommendations: form.recommendations || null,
                    };
                    if (form.override_enabled && form.override_composite !== '') {
                        body.override_composite = parseFloat(form.override_composite);
                        body.override_band = bandFor(parseFloat(form.override_composite));
                        body.override_reason = form.override_reason;
                    }
                    await api('/api/v1/assessments/rubric/sign', { method: 'POST', body: JSON.stringify(body) }, token);
                    toast('Risk score signed off');
                    onSuccess?.();
                    onClose();
                } catch (err) { setError(err.message); }
                finally { setSubmitting(false); }
            };

            // Helper — is this layer score still equal to the auto-suggested one?
            const isAuto = (name, autoLayer) => {
                if (!autoLayer) return false;
                return Math.abs(parseFloat(form[name]) - autoLayer.score) < 0.05;
            };

            const ScoredLayer = ({ label, name, autoLayer }) => {
                const overridden = autoLayer && !isAuto(name, autoLayer);
                return (
                    <div className="p-3" style={{background: 'var(--zone-2)', border: '1px solid rgba(201,169,110,0.12)'}}>
                        <div className="flex justify-between items-start mb-2">
                            <label className="text-xs font-mono uppercase tracking-widest text-muted">{label}</label>
                            {autoLayer && (
                                <span className="text-xs font-mono"
                                      style={{color: overridden ? 'var(--amber)' : 'var(--gold-dim)'}}>
                                    {overridden ? '⟂ analyst override' : '⟁ auto'}
                                </span>
                            )}
                        </div>
                        <input
                            type="number" step="0.1" min="1" max="5"
                            value={form[name]}
                            onChange={e => setForm({...form, [name]: e.target.value})}
                            required
                            style={{width: '100%'}}
                        />
                        {autoLayer && autoLayer.rationale && (
                            <div className="text-xs text-muted mt-2 leading-snug">{autoLayer.rationale}</div>
                        )}
                    </div>
                );
            };

            return (
                <Dialog onClose={onClose} wide title={supersede ? 'Update Risk Assessment' : 'Record Risk Assessment'}>
                    <form onSubmit={submit} className="space-y-4">
                        {/* Optional: refresh every source (mentions + breach + sanctions)
                            then re-score, so you sign against a current picture. */}
                        <div className="flex items-center justify-between gap-3 p-3" style={{background:'var(--zone-2)', border:'1px solid var(--rule-2)', borderRadius:'var(--radius-sm)'}}>
                            <div className="text-xs text-muted">
                                {refreshing
                                    ? <span style={{color:'var(--cyan)'}}>{refreshMsg || 'Refreshing…'}</span>
                                    : 'Score uses the data currently on file. Refresh first to pull the latest from every source.'}
                            </div>
                            <button type="button" className="btn-ghost" onClick={refreshAllSources} disabled={refreshing || submitting}>
                                {refreshing ? 'Refreshing…' : '🤖 Refresh all sources & re-score'}
                            </button>
                        </div>
                        {autoLoading && (
                            <div className="p-3 text-xs font-mono text-muted" style={{background: 'rgba(201,169,110,0.05)'}}>
                                Loading computed risk score…
                            </div>
                        )}
                        {autoError && (
                            <div className="p-3 text-xs font-mono text-[var(--red)]" style={{background: 'rgba(157,59,59,0.05)'}}>
                                Could not load risk score: {autoError}. Generate a report first to compute one.
                            </div>
                        )}
                        {rubric && (
                            <>
                            <div className="p-3 text-xs font-mono text-muted leading-relaxed" style={{background: 'rgba(201,169,110,0.05)', border: '1px solid rgba(201,169,110,0.15)'}}>
                                <span className="gold">Review the computed risk score below.</span> You are the named authority — sign off to confirm, or override the score with a reason where your judgement differs. Your name enters the audit chain.
                            </div>

                            <div className="panel p-4">
                                <div className="flex items-end gap-4 mb-3">
                                    <div className="font-display gold" style={{fontSize:'2.5rem', lineHeight:0.9}}>{Number(effComposite).toFixed(1)}</div>
                                    <div className="pb-1">
                                        <div className="font-mono text-sm uppercase tracking-widest" style={{color: RATINGS.hex(Number(effComposite))}}>{RATINGS.label(Number(effComposite))}</div>
                                        <div className="font-mono text-xs text-muted">out of 5.0{overrideComposite!=null ? ` · overridden from ${Number(rubric.composite).toFixed(1)}` : ''}</div>
                                    </div>
                                </div>
                                <table className="w-full" style={{borderCollapse:'collapse'}}>
                                    <tbody>
                                        {rubric.inputs.map((inp) => (
                                            <tr key={inp.name} className="border-t border-[var(--line)]">
                                                <td className="py-2 pr-3">
                                                    <span className="text-paper text-sm">{inp.label}</span>
                                                    <span className="font-mono text-xs text-muted ml-2">{inp.reason}</span>
                                                </td>
                                                <td className="py-2 text-right font-mono text-sm whitespace-nowrap">
                                                    {inp.score == null
                                                        ? <span className="text-muted" title="Not assessed — no collection run recorded for this signal">—</span>
                                                        : <><span className="gold">{inp.score}</span><span className="text-muted text-xs">/5</span></>}
                                                    <span className="text-muted text-xs ml-2">×{inp.weight.toFixed(1)}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="p-3" style={{background:'var(--zone-2)', border:'1px solid rgba(201,169,110,0.12)'}}>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={form.override_enabled} onChange={e => setForm({...form, override_enabled: e.target.checked})} />
                                    <span className="text-xs font-mono uppercase tracking-widest text-muted">Override the computed score</span>
                                </label>
                                {form.override_enabled && (
                                    <div className="mt-3 space-y-2">
                                        <div className="flex items-center gap-3">
                                            <input type="number" step="0.1" min="0" max="5" value={form.override_composite}
                                                onChange={e => setForm({...form, override_composite: e.target.value})}
                                                placeholder="e.g. 2.5" style={{width:'120px'}} />
                                            <span className="font-mono text-xs text-muted">→ {form.override_composite !== '' ? bandFor(parseFloat(form.override_composite)||0) : '—'}</span>
                                        </div>
                                        <textarea rows="2" value={form.override_reason}
                                            onChange={e => setForm({...form, override_reason: e.target.value})}
                                            placeholder="Reason for overriding the computed score (required)."></textarea>
                                    </div>
                                )}
                            </div>
                            </>
                        )}

                        <div className="p-3" style={{background: 'rgba(201,169,110,0.05)', border: '1px solid rgba(201,169,110,0.18)'}}>
                            <div className="flex justify-between items-center mb-2">
                                <div className="font-mono text-xs uppercase tracking-widest gold">Narrative Draft (LLM-Assisted)</div>
                                <button type="button"
                                    className="btn-ghost"
                                    onClick={generateNarrative}
                                    disabled={narrativeLoading}
                                    style={{fontSize: '11px', padding: '4px 10px'}}>
                                    {narrativeLoading ? 'Drafting…' :
                                     narrativeDraft ? '⟳ Regenerate' : 'Generate Narrative Draft'}
                                </button>
                            </div>
                            <div className="text-xs text-muted">
                                Asks Claude to write a client-facing summary and draft recommendations based on the auto-score. The analyst reviews, edits if needed, and signs off — every word that's published carries your name in the audit chain.
                            </div>
                            {narrativeError && <div className="text-[var(--red)] text-xs mt-2">{narrativeError}</div>}
                            {narrativeDraft && (
                                <div className="text-xs text-muted font-mono mt-2">
                                    Model: {narrativeDraft.model} · {narrativeDraft.input_tokens + narrativeDraft.output_tokens} tokens used
                                </div>
                            )}
                        </div>

                        <div>
                            <label>Assessment Summary *</label>
                            <textarea rows="16" style={{minHeight:'320px', lineHeight:1.6, fontSize:'15px'}} value={form.assessment_summary} onChange={e => setForm({...form, assessment_summary: e.target.value})} required
                                placeholder="Your narrative judgement: what are the dominant drivers? What changed since last assessment?"></textarea>
                            {autoScore && (
                                <div className="text-xs text-muted font-mono mt-1">
                                    {narrativeDraft
                                        ? 'Pre-filled from LLM draft. Edit freely — this is your analysis on the record.'
                                        : 'Pre-filled from auto-score. Use the "Generate Narrative Draft" button above for a richer client-facing version, or edit as is.'}
                                </div>
                            )}
                        </div>

                        <div>
                            <label>Recommendations</label>
                            <textarea rows="8" style={{minHeight:'160px', lineHeight:1.6, fontSize:'15px'}} value={form.recommendations} onChange={e => setForm({...form, recommendations: e.target.value})}
                                placeholder="Concrete actions the client should consider."></textarea>
                            {narrativeDraft && form.recommendations && (
                                <div className="text-xs text-muted font-mono mt-1">
                                    Pre-filled from LLM draft. The first line should remain "Draft for analyst review — verify before sharing with client." Replace or remove that line once you've reviewed.
                                </div>
                            )}
                        </div>

                        <div className="text-xs text-muted">
                            High and critical bands require lead_analyst or admin role. Your name and reasoning are recorded in the audit chain.
                        </div>
                        {error && <div className="text-[var(--red)] text-sm">{error}</div>}
                        <div className="flex justify-end gap-3 pt-2">
                            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                            <button type="submit" className="btn-gold" disabled={submitting || autoLoading}>{submitting ? 'Recording…' : 'Sign Off Assessment'}</button>
                        </div>
                    </form>
                </Dialog>
            );
        }

export { AssessmentDialog };
